import type { ProgramOutcome, ProgramRow } from '@@/api/program';
// The file, not the barrel: the barrel re-exports the handler, which would pull the
// database clients into the dashboard bundle.
import { SIGNIFICANT_OUTCOMES } from '@@/api/program/classify-outcome';
import { CUSTOM_MECHANISM_LABEL, mechanismLabel, parseMechanisms } from '../experiments/expected-mechanisms';

/**
 * Everything the programme screen shows, derived from the rows in one pass.
 *
 * Pure and separate from the hook so the arithmetic is testable without React or a
 * server. Month buckets are the viewer's local months, which is why the server sends
 * rows rather than a finished shape.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Below this many decided tests a rate is reported but flagged. Five is not a
 * statistical threshold; it is the point below which one result moves the rate by
 * twenty points, and a reader should see that before comparing two rows.
 */
export const MIN_READABLE = 5;

export type OutcomeCounts = Record<ProgramOutcome, number>;

export type MonthBucket = OutcomeCounts & {
  /** `YYYY-MM`, local. */
  key: string;
  label: string;
  launched: number;
};

export type BreakdownRow = {
  key: string;
  label: string;
  /**
   * Concluded tests whose statistics were significant (won, lost or mixed), which is
   * the win rate's denominator. Read on the statistics alone, so a deployment of an
   * inconclusive test does not enter it; see tally.
   */
  decided: number;
  won: number;
  lost: number;
  /** Percent, or null with nothing decided. */
  winRate: number | null;
  /** Median point-estimate lift of the winners, in percent. */
  medianWinLift: number | null;
};

export type ProgramSummary = {
  launched: number;
  inFlight: number;
  concluded: number;
  /**
   * The win rate's denominator: concluded tests the programme counts as decided, which
   * is won, lost or mixed after a deployment has counted as a win.
   */
  decided: number;
  /** Programme outcomes: a deployment counts as a win. */
  counts: OutcomeCounts;
  /** How many of `counts.won` are wins only because a variant was deployed. */
  wonByDeployment: number;
  winRate: number | null;
  medianWinLift: number | null;
  /** How many wins had a measured lift, so the median can say what it is over. */
  winLiftCount: number;
  medianDurationDays: number | null;
  /** Creation to start, for tests launched in the window. */
  medianLeadDays: number | null;
  /**
   * Statistical winners, and how many of them were ever deployed or implemented.
   * Taken over the statistics alone: counting deployment wins would make every
   * deployed test a shipped winner by definition, so the rate could only rise.
   */
  statisticalWinners: number;
  winnersShipped: number;
  shippedRate: number | null;
  months: MonthBucket[];
  mechanisms: BreakdownRow[];
  pxlBands: BreakdownRow[];
  /** Newest conclusion first. */
  concludedRows: ProgramRow[];
};

const emptyCounts = (): OutcomeCounts => ({ won: 0, lost: 0, mixed: 0, inconclusive: 0, 'no-data': 0 });

export const median = (values: readonly number[]): number | null => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle]! : (sorted[middle - 1]! + sorted[middle]!) / 2;
};

const percent = (part: number, whole: number): number | null => (whole > 0 ? (part / whole) * 100 : null);

const monthKey = (at: number): string => {
  const date = new Date(at);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const MONTH_LABEL = new Intl.DateTimeFormat('en', { month: 'short', year: '2-digit' });

/** Every local month from the one containing `since` to the one containing `now`. */
export function monthRange(since: number, now: number): MonthBucket[] {
  const buckets: MonthBucket[] = [];
  const cursor = new Date(since);
  cursor.setDate(1);
  cursor.setHours(0, 0, 0, 0);

  while (cursor.getTime() <= now) {
    const at = cursor.getTime();
    buckets.push({ key: monthKey(at), label: MONTH_LABEL.format(cursor), launched: 0, ...emptyCounts() });
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return buckets;
}

/** The local month start `months - 1` months before now: a window of whole months. */
export function windowStart(months: number, now: number): number {
  const date = new Date(now);
  return new Date(date.getFullYear(), date.getMonth() - (months - 1), 1).getTime();
}

type Accumulator = { decided: number; won: number; lost: number; winLifts: number[] };

const toBreakdown = (key: string, label: string, acc: Accumulator): BreakdownRow => ({
  key,
  label,
  decided: acc.decided,
  won: acc.won,
  lost: acc.lost,
  winRate: percent(acc.won, acc.decided),
  medianWinLift: median(acc.winLifts),
});

/**
 * One test into a breakdown, on its statistical outcome.
 *
 * The breakdowns ask whether a mechanism or a PXL score predicts winning, and a
 * deployment is a choice made after the fact, most often for the tests already rated
 * important. Counting it would make high importance "win" by construction.
 */
const tally = (acc: Accumulator | undefined, row: ProgramRow): Accumulator => {
  const base = acc ?? { decided: 0, won: 0, lost: 0, winLifts: [] };
  const outcome = row.reading?.statisticalOutcome;
  const lift = row.reading?.lift;

  return {
    decided: base.decided + 1,
    won: base.won + (outcome === 'won' ? 1 : 0),
    lost: base.lost + (outcome === 'lost' ? 1 : 0),
    winLifts: outcome === 'won' && typeof lift === 'number' ? [...base.winLifts, lift] : base.winLifts,
  };
};

/**
 * The mechanism keys a test counts under. A test with several mechanisms counts under
 * each, so the rows do not sum to the total; one with none counts as "not stated",
 * which is itself worth seeing — it is the share of the programme with no theory.
 */
const mechanismKeys = (raw: unknown): string[] => {
  const { selected, custom } = parseMechanisms(raw);
  const keys = [...new Set(selected)];
  if (custom.trim()) keys.push('custom');
  return keys.length ? keys : ['none'];
};

const mechanismKeyLabel = (key: string): string => {
  if (key === 'custom') return CUSTOM_MECHANISM_LABEL;
  if (key === 'none') return 'Not stated';
  return mechanismLabel(key);
};

export const PXL_BANDS = [
  { key: 'high', label: 'High importance (7-10)' },
  { key: 'medium', label: 'Medium importance (4-6)' },
  { key: 'low', label: 'Low importance (0-3)' },
  { key: 'unscored', label: 'Not PXL-scored' },
] as const;

const pxlBand = (importance: number | null): string => {
  if (importance === null) return 'unscored';
  if (importance >= 7) return 'high';
  if (importance >= 4) return 'medium';
  return 'low';
};

type SummariseParams = { rows: readonly ProgramRow[]; since: number; now: number };

export function summariseProgram({ rows, since, now }: SummariseParams): ProgramSummary {
  const months = monthRange(since, now);
  const monthIndex = new Map(months.map((bucket, index) => [bucket.key, index]));
  const bump = (at: number, field: keyof OutcomeCounts | 'launched') => {
    const index = monthIndex.get(monthKey(at));
    if (index === undefined) return;
    months[index] = { ...months[index]!, [field]: months[index]![field] + 1 };
  };

  const counts = emptyCounts();
  const winLifts: number[] = [];
  const durations: number[] = [];
  const leads: number[] = [];
  const mechanisms = new Map<string, Accumulator>();
  const bands = new Map<string, Accumulator>();
  const concludedRows: ProgramRow[] = [];

  let launched = 0;
  let inFlight = 0;
  let wonByDeployment = 0;
  let statisticalWinners = 0;
  let winnersShipped = 0;

  for (const row of rows) {
    const { concludedAt, reading } = row;

    // A test with no stored statistics says nothing about the programme, so it is left
    // off the screen entirely, deployed or not: not listed, not charted, and not in any
    // count, launches and lead time included. Checked first for that reason. Running
    // tests have no reading yet, so they are never caught here.
    if (reading?.outcome === 'no-data') continue;

    if (row.status === 'Running') inFlight += 1;

    if (row.startedAt >= since) {
      launched += 1;
      bump(row.startedAt, 'launched');
      if (row.createdAt !== null && row.createdAt <= row.startedAt) leads.push(row.startedAt - row.createdAt);
    }

    if (concludedAt === null || concludedAt < since || !reading) continue;

    concludedRows.push(row);
    counts[reading.outcome] += 1;
    bump(concludedAt, reading.outcome);
    if (concludedAt >= row.startedAt) durations.push(concludedAt - row.startedAt);

    if (reading.outcome === 'won') {
      if (reading.byDeployment) wonByDeployment += 1;
      if (reading.lift !== null) winLifts.push(reading.lift);
    }

    // Shipping is measured over statistical winners only; see ProgramSummary.
    // deployedAt rather than status: a winner deployed and later ended still shipped.
    if (reading.statisticalOutcome === 'won') {
      statisticalWinners += 1;
      if (row.deployedAt !== null || row.implementedAt !== null) winnersShipped += 1;
    }

    // Breakdowns read the statistics alone (see tally), so a test enters them only
    // when its statistical result was significant.
    if (!SIGNIFICANT_OUTCOMES.includes(reading.statisticalOutcome)) continue;

    for (const key of mechanismKeys(row.expectedMechanisms)) mechanisms.set(key, tally(mechanisms.get(key), row));
    const band = pxlBand(row.importance);
    bands.set(band, tally(bands.get(band), row));
  }

  const concluded = concludedRows.length;
  const decided = SIGNIFICANT_OUTCOMES.reduce((total, outcome) => total + counts[outcome], 0);

  const mechanismRows = [...mechanisms.entries()]
    .map(([key, acc]) => toBreakdown(key, mechanismKeyLabel(key), acc))
    .sort((a, b) => b.decided - a.decided || a.label.localeCompare(b.label));

  const pxlRows = PXL_BANDS.flatMap(({ key, label }) => {
    const acc = bands.get(key);
    return acc ? [toBreakdown(key, label, acc)] : [];
  });

  const medianDuration = median(durations);
  const medianLead = median(leads);

  return {
    launched,
    inFlight,
    concluded,
    decided,
    counts,
    wonByDeployment,
    winRate: percent(counts.won, decided),
    medianWinLift: median(winLifts),
    winLiftCount: winLifts.length,
    medianDurationDays: medianDuration === null ? null : medianDuration / DAY_MS,
    medianLeadDays: medianLead === null ? null : medianLead / DAY_MS,
    statisticalWinners,
    winnersShipped,
    shippedRate: percent(winnersShipped, statisticalWinners),
    months,
    mechanisms: mechanismRows,
    pxlBands: pxlRows,
    concludedRows: concludedRows.sort((a, b) => (b.concludedAt ?? 0) - (a.concludedAt ?? 0)),
  };
}
